import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {focusRing} from "../../../Styles/media";

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'solid'
export type ButtonSize = 'sm' | 'md' | 'lg'

const sizes = {
    sm: css`
        min-height: 34px;
        padding: 0 14px;
        font-size: 13px;
        border-radius: ${({theme}) => theme.radius.sm};
    `,
    md: css`
        min-height: 44px;
        padding: 0 20px;
        font-size: 14px;
        border-radius: ${({theme}) => theme.radius.sm};
    `,
    lg: css`
        min-height: 54px;
        padding: 0 28px;
        font-size: 15px;
        border-radius: ${({theme}) => theme.radius.md};
    `
}

const variants = {
    primary: css`
        background-color: ${({theme}) => theme.accent};
        color: ${({theme}) => theme.accentContrast};
        border-color: transparent;

        &:hover:not(:disabled) {
            background-color: ${({theme}) => theme.accentHover};
            box-shadow: ${({theme}) => theme.shadow.accent};
            transform: translateY(-1px);
        }
    `,
    solid: css`
        background-color: ${({theme}) => theme.color};
        color: ${({theme}) => theme.colorInverted};
        border-color: transparent;

        &:hover:not(:disabled) {
            background-color: #ffffff;
            transform: translateY(-1px);
        }
    `,
    outline: css`
        background-color: transparent;
        color: ${({theme}) => theme.color};
        border-color: ${({theme}) => theme.lineStrong};

        &:hover:not(:disabled) {
            background-color: rgba(255, 255, 255, 0.06);
            border-color: ${({theme}) => theme.color};
        }
    `,
    ghost: css`
        background-color: transparent;
        color: ${({theme}) => theme.colorMuted};
        border-color: transparent;

        &:hover:not(:disabled) {
            background-color: rgba(255, 255, 255, 0.06);
            color: ${({theme}) => theme.color};
        }
    `
}

const Button = styled.button<{
    $variant: ButtonVariant
    $size: ButtonSize
    $fullWidth: boolean
}>`
    ${focusRing};
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: ${({$fullWidth}) => ($fullWidth ? '100%' : 'auto')};
    border: 1px solid transparent;
    font-weight: 600;
    letter-spacing: 0.01em;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    transition: background-color 240ms ${({theme}) => theme.ease.out},
    color 240ms ${({theme}) => theme.ease.out},
    border-color 240ms ${({theme}) => theme.ease.out},
    box-shadow 240ms ${({theme}) => theme.ease.out},
    transform 240ms ${({theme}) => theme.ease.out};

    ${({$size}) => sizes[$size]};
    ${({$variant}) => variants[$variant]};

    &:active:not(:disabled) {
        transform: translateY(1px);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`

const Icon = styled(FontAwesomeIcon)`
    width: 15px;
    height: 15px;
    flex-shrink: 0;
`

export const Styled = {
    Button,
    Icon
}
